import { createContext, useContext } from 'react';
import { ELinxConnectionMethod, EmbedUrlRequest } from 'vopay';
import { EventMessageData } from './ELinxConnect';

// The embed endpoint also takes a comma-separated list of methods, which vopay's own
// EmbedUrlRequest.PaymentSelectionMethod expresses as `ELinxConnectionMethod | string` — i.e. it
// checks nothing. This wrapper narrows it back down to the combinations the portals actually send;
// a new one is a one-word addition here.
export type ELinxConnectionMethods = ELinxConnectionMethod | 'bank,credit';

// Caller-facing subset of the embed request — the fixed fields (RedirectURL/RedirectMethod) and the
// ClientAccountID/ContactID branch are filled in by the provider.
export type ELinxConnectRequest = Omit<
  EmbedUrlRequest,
  'RedirectURL' | 'RedirectMethod' | 'ClientAccountID' | 'ContactID' | 'PaymentSelectionMethod'
> & {
  PaymentSelectionMethod: ELinxConnectionMethods;
};

// Raises the overlay from the raw inputs; resolves with the whole link payload (rejects if closed first).
// It resolves the payload rather than just Token because a caller that opens eLinx with more than one
// PaymentSelectionMethod (e.g. 'bank,credit') can only learn what the user actually linked from the
// payload's PaymentMethod. The host-specific values (country/clientAccountsEnabled) are supplied by the
// caller — a host's thin adapter typically binds them from its own account context so its callers don't
// have to.
export type OpenELinxConnect = (
  values: ELinxConnectRequest,
  country: EmbedUrlRequest['Country'],
  clientAccountsEnabled: boolean,
  accountID?: string,
) => Promise<EventMessageData>;

export const ELinxConnectContext = createContext<{ open: OpenELinxConnect } | null>(null);

// Pass-through accessor for the shared-owned eLinx flow (mirrors useDialog/useAlertDialog). The overlay
// is rendered once by ELinxConnectProvider (composed into SharedProviders). Hosts usually consume this
// through a thin adapter that binds country/clientAccountsEnabled — see client-dashboard's useELinxConnect.
export default function useELinxConnect() {
  const eLinxConnect = useContext(ELinxConnectContext);

  if (!eLinxConnect) {
    throw new Error("useELinxConnect: no ELinxConnectProvider mounted. Mount <SharedProviders> (from 'shared').");
  }

  return eLinxConnect;
}
